export enum  MediaType {
  VIDEO = "video",
  AUDIO = "audio"
}

export enum CopyJobStatus {
  CREATED = "created",
  ENQUEUED = "enqueued",
  UPLOADING = "uploading",
  UPLOAD_START_FAILURE = "upload_start_failure",
  UPLOAD_FAILURE = "upload_failure",
  UPLOAD_TIMEOUT = "upload_timeout",
  CANCELED = "canceled",
  FINISHED = "finished",
  FAILURE = "failure"
}

export enum SourceType {
  HTTP = "http"
}

export enum DestinationType {
  S3 = "S3"
}

export interface Link {
  id: string
  collection: string
}

export interface Meta {
  version: number
  createdBy: string
  createdAt: Date
  modifiedBy: string
  modifiedAt: Date
}

interface Base {
  uuid: string
  contractId: string
  meta: Meta
}

export interface Timeline {
  startDateTime: string
  endDateTime: string
  durationMs: number
}

export enum TypedTimelineType {
  GAP = "gap",
  RECORDER = "recorder"
}

export interface TypedTimeline extends Timeline {
  type: TypedTimelineType
}

export interface Source {
  type: SourceType
  name: string
  description: string
}

export interface HTTPSource extends Source{
  path: string
  headers: object
}

export interface Destination extends Base {
  type: DestinationType
  name: string
  description: string
}

export interface S3 extends Destination {
  type: DestinationType
  endpoint: string
  accessKeyId: string
  secretAccessKey: string
  bucket: string
  region: string
  path: string
} 

export interface CopyJob extends Base {
  status: CopyJobStatus
  statusHistory: CopyJobStatus[]
  location: Location
  sourceType: SourceType
  source: Source | HTTPSource
  destinationType: DestinationType
  destination: Destination | S3
}

export interface CopyJobStatusView extends StatusView {}

export interface CopyManager extends Base {
  url: string
}

export interface Compositor extends Base {
  url: string
  active: boolean
  healthy: boolean
  maxJobs: number
  runningJobs: number
  prioritization: number
  mediastoreExportPath: string
  healthCheck: string
  policies: string
  apiMediastoreInfo: string
  apiMediastoreMakeCut: string
  apiFilesDownload: string
  apiPrefix: string
}

export interface Location extends Base {
  compositor: Compositor | Link
  copyManager: CopyManager | Link
  tags: { [key: string]: string }
  timeline: Timeline[]
  active: boolean
}

export interface StreamKey {
  streamId: number
  programNumber: number
  sourceName: string
  renditionName: string
}

export interface Framerate {
  frames: number
  seconds: number
}

export interface StreamValue {
  metadataType: MediaType
  codec: string
  sampleRate: number | null
  channelLayout: string | null
  bitrate: number
  language: string | null
  width: number | null
  height: number | null
  frameRate: Framerate | null
}

export interface StreamLocation {
  active: boolean
  location: Location
  timeline: Timeline[]
}

export interface Stream extends Base {
  streamKey: StreamKey
  type: MediaType
  value: StreamValue
  locations: StreamLocation[]
}

export interface Channel extends Base {
  name: string
  type: "recorder" | "asset"
  mediastorePath: string
  externalId: null | string
  active: boolean
  timelines: { [key: string]: Timeline}
  streams: Stream[] | Link[]
}

export interface Contract {
  id: string
  name: string
}

interface Policy {
  id: string
  name: string
  obj: string
  act: string
  implicit: string | null
  appName: string
  appId: string
  appType: string
}

interface UserData {
  id: string
  externalId: string
  email: string
  firstName: string
  lastName: string
  isMaster: boolean
}

interface Token {
  token: string
  type: string
  exp: string
}

interface App {
  id: string
  name: string
  policyDefinition: string
  type: string
}

export interface User {
  data: UserData
  token: Token
  contracts: Contract[]
  policies: Policy[]
  apps: App[]
}

interface PaginationMeta {
  count: number
  limit: number
  offset: number
  total: number
}

export interface Pagination<T> {
  list: Array<T>
  meta: PaginationMeta
}

enum ExportStrategy {
  HIGHEST_WITH_AUDIO = "highest_with_audio"
}

export interface Webhook {
  url: string
  headers?: object
  notifyWhen?: string[] | null
}

export interface JobDestination {
  filenameTemplate: string
  pathTemplate: string
  destinationUuid: string
  fileFilter?: string | null
}

export interface Simplecut {
  title: string
  channel: string
  video: string
  audio: string
  exportStrategy?: ExportStrategy
  startDateTime: string
  endDateTime: string
  frameAccurate?: boolean
  fastStart?: boolean
  skipGapSeconds?: number
  locationFilter?: object
  audioLanguageRewrite?: object
  webhooks?: Webhook[]
  destinations?: JobDestination[]
  metadata?: any
}

export interface ValidationError {
  detail: string
}

export enum StatusEnum {
  CREATED = "created",
  ENQUEUED = "enqueued",
  GENERATING = "generating",
  FAILED_GENERATION = "failed_generating",
  FINISHED_GENERATION = "finished_generating",
  UPLOADING = "uploading",
  FINISHED = "finished",
  FAILURE = "failure",
  CANCELED = "canceled",
}

export interface Status {
  status: StatusEnum
  progress: number
  message: string
  detail: object
  timestamp: Date
}

export interface StatusView {
  uuid: string
  status: Status
  statusHistory: Status[]
}

export interface Cut {
  start: string
  end: string
  durationMs: number
  streams: Stream[]
  mediastore: Channel
}

interface SharedJobFileAttributes {
  title: string
  prioritization: number
  availableLocations: Location[]
  metadata: any
  frameAccurate: boolean
  fastStart: boolean
  status: Status
  statusHistory: Status[]
}

export interface Job extends Base, SharedJobFileAttributes {
  cuts: Cut[]
  audioLanguageRewrite: object
  exportStrategy: ExportStrategy
  destinations: JobDestination[]
  files: File[]
  webhooks: Webhook[]
  skipGapSeconds: number
  locationFilter: object
}

export interface JobStatus extends StatusView {
  title: string
}

export interface JobStatusTree extends JobStatus {
  title: string
  files: FileStatusTree[]
}

export interface File extends Base, SharedJobFileAttributes {
}

export interface FileStatus extends StatusView {
  title: string
}

export interface FileStatusTree extends FileStatus {
  copyJobs: CopyJobStatusView[]
}

export interface Config {
  isStandalone?: boolean
  apiUrl?: string
  basePath?: string
  contract?: Contract
  user?: User
  language?: "en" | "de"
}

export interface LocationMergedTimeline {
  locationId: string
  locationUuid: string
  isValid: boolean
  maxGapSizeSeconds: number
  sumGapSeconds: number
  numGaps: number
  tags: object
  timeline: TypedTimeline[]
}